import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  ListItemButton,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material'
import ListItemDefault from '../../../../../components/ListItem/ListItemDefault'
import { RecipientStatus } from '../../../../settings/TenantBalanceWithdraw/RecipientStatus'
import SwitchRoundSmall from '../../../../../components/Switch/SwitchRoundSmall'
import { ITenantBalance, TTenantSettings } from '../../../../../core/types/Tenant'
import { HtmlTooltip } from '../../../../../components/Tooltip/HtmlTooltip'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
  IconCopy,
  IconWhatsApp,
} from '../../../../../core/constants/IconConsts'
import { maskMoney, maskPhoneNumber } from '../../../../../masks/masks'
import { red } from '@mui/material/colors'
import { useAdminUserContext } from '../../../../../core/hooks/useAdminUserContext'
import { useContext, useEffect, useState } from 'react'
import { AuthContext } from '../../../../../core/context/AuthContext'
import UserAccountPermissionController from '../../../../../core/controllers/UserAccountPermissionController'
import { TenantController } from '../../../../../core/controllers/TenantController'
import { useToast } from '../../../../../core/context/ToastContext'
import { useNavigate } from 'react-router-dom'
import LoginIcon from '@mui/icons-material/Login'
import ErrorSnackbar from '../../../../../components/Snackbar/ErrorSnackbar'
import { IErrorResponse } from '../../../../../core/types/ErrorResponse'
import { Html } from '@mui/icons-material'
import { AccountOperationalStatus } from '../../../../../components/Tenants/AccountOperationalStatus'

export type IUserSettingGeneralChangeEvent = (
  e: React.ChangeEvent<HTMLInputElement>
) => Promise<void>

export interface IUserSettingGeneralProps {
  loadingSettings: boolean
  settings: TTenantSettings | null
  errorWithdrawLimit: string
  setErrorWithdrawLimit: (value: string) => void
  handleChangeCopyRecipientId: () => void
  handleOpenRecipient: () => void
  handleChangeSalesEnabled: IUserSettingGeneralChangeEvent
  handleChangeWithdrawlsEnabled: IUserSettingGeneralChangeEvent
  handleChangeEnableRegisterProducts: IUserSettingGeneralChangeEvent
  handleChangeRequestValidationForProducts: IUserSettingGeneralChangeEvent
  handleChangeCeletusClub: IUserSettingGeneralChangeEvent
}

const UserSettingGeneral = ({
  loadingSettings,
  settings,
  errorWithdrawLimit,
  setErrorWithdrawLimit,
  handleChangeCopyRecipientId,
  handleOpenRecipient,
  handleChangeSalesEnabled,
  handleChangeWithdrawlsEnabled,
  handleChangeEnableRegisterProducts,
  handleChangeRequestValidationForProducts,
  handleChangeCeletusClub,
}: IUserSettingGeneralProps) => {
  const { setSession } = useContext(AuthContext)
  const { tenantSettings, setTenantSettings } = useAdminUserContext()
  const navigate = useNavigate()
  const { showToast } = useToast()

  const [error, setError] = useState<string>('')
  const [loadingInternational, setLoadingInternational] = useState(false)

  const [limitLoading, setLimitLoading] = useState(false)

  useEffect(() => {
    if (!tenantSettings?.tenantId) return

    setLimitLoading(true)
    TenantController.getBalance({ tenantId: tenantSettings.tenantId }).then(
      (response) => {
        const errorResponse = response as IErrorResponse
        const balanceData = response as ITenantBalance
        if (!errorResponse.code) {
          setTenantSettings({
            ...tenantSettings,
            withdrawLimitEnabled: balanceData.withdrawLimitEnabled ?? false,
            withdrawLimit: balanceData.withdrawLimit ?? 0,
          })
        } else {
          setError(errorResponse.error)
        }
        setLimitLoading(false)
      }
    )
  }, [tenantSettings?.tenantId])

  const handleChangeInternationalSales = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!tenantSettings?.tenantId) return
    setLoadingInternational(true)
    const enabled = e.target.checked
    const result = await TenantController.setInternationalSalesEnabled({
      tenantId: tenantSettings.tenantId,
      enabled,
    })
    setLoadingInternational(false)
    if (result.success) {
      setTenantSettings({ ...tenantSettings, internationalSalesEnabled: enabled })
      showToast('Configuração atualizada', 'success')
    } else {
      showToast(result.error || 'Erro ao atualizar configuração', 'error')
    }
  }

  function handleChangeOpenWhatsApp(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void {
    const phoneNumber = tenantSettings?.tenantPhone
    if (phoneNumber) {
      const formattedPhoneNumber = phoneNumber.replace(/\D/g, '') // Remove non-numeric characters
      const whatsappUrl = `https://wa.me/${formattedPhoneNumber}`
      window.open(whatsappUrl, '_blank')
    }
  }

  const handleChangeAccount = async () => {
    if (!settings?.permissionCodeAdmin) 
      return;

    const {
      success: successCollaborator,
      data: collaborator,
      error: errorCollaborator,
    } = await UserAccountPermissionController.getCollaborator({
      codeId: settings.permissionCodeAdmin,
    })

    if (!successCollaborator) {
      setError(`Erro ao buscar colaborador. ${errorCollaborator}`)
      return
    }

    if (collaborator) {
      const res = await UserAccountPermissionController.changeTenantAccountUser(
        {
          userAccountPermissionCodeId: collaborator.codeId,
        }
      )

      if (!res.success) {
        setError(res.error)
        return
      }

      if (res.success && res.data) {
        setSession({
          id: collaborator.codeId,
          codeId: settings.permissionCodeAdmin,
          name: settings.tenantName ?? '',
          email: settings.tenantEmail ?? '',
          token: res.data,
        })
        navigate('/')
      }
    }
  }

  return (
    <>
      <Stack direction="column" spacing={0} sx={{ py: 1 }}>
        <ListItemDefault
          title="Código do Recebedor"
          widthTitlePercentage={40}
          variant="compact"
          value={
            <Box>
              {(tenantSettings?.recipientId ?? '') !== '' && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <ListItemButton onClick={handleChangeCopyRecipientId}>
                    <Typography noWrap variant="subtitle2">
                      {tenantSettings?.recipientId}
                    </Typography>
                  </ListItemButton>
                  <HtmlTooltip title="Copiar código do recebedor">
                    <IconButton
                      size="small"
                      onClick={handleChangeCopyRecipientId}
                    >
                      <img src={IconCopy} alt="copy" />
                    </IconButton>
                  </HtmlTooltip>
                  <HtmlTooltip title="Abrir recebedor na Pagar.Me">
                    <IconButton size="small" onClick={handleOpenRecipient}>
                      <OpenInNewIcon color="primary" />
                    </IconButton>
                  </HtmlTooltip>
                </Stack>
              )}
            </Box>
          }
        />
        {(tenantSettings?.recipientId ?? '') !== '' && (
          <ListItemDefault
            title="Status do Recebedor"
            widthTitlePercentage={40}
            variant="compact"
            value={
              !loadingSettings ? (
                <RecipientStatus status={settings?.recipientStatus ?? ''} />
              ) : (
                <CircularProgress size={27} />
              )
            }
          />
        )}
        <ListItemDefault
          title="Telefone de contato"
          widthTitlePercentage={40}
          variant="compact"
          value={
            <Box>
              {(tenantSettings?.recipientId ?? '') !== '' && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography noWrap variant="subtitle2">
                    {maskPhoneNumber(tenantSettings?.tenantPhone)}
                  </Typography>
                  <HtmlTooltip title="Abrir conversa no WhatsApp">
                    <IconButton size="small" onClick={handleChangeOpenWhatsApp}>
                      <img src={IconWhatsApp} alt="whatsapp" />
                    </IconButton>
                  </HtmlTooltip>
                </Stack>
              )}
            </Box>
          }
        />

        <ListItemDefault
          title={
            <HtmlTooltip title="Quando ativado, o produtor poderá cadastrar produtos.">
              <FormControlLabel
                control={
                  <SwitchRoundSmall
                    sx={{ m: 1 }}
                    checked={tenantSettings?.enableRegisterProducts ?? false}
                    onChange={handleChangeEnableRegisterProducts}
                  />
                }
                label={
                  <Typography variant="subtitle2">
                    Permite cadastrar produtos
                  </Typography>
                }
                sx={{ width: '100%' }}
              />
            </HtmlTooltip>
          }
          widthTitlePercentage={50}
          variant="compact"
          value={
            <HtmlTooltip title="Quando ativado, todos os novos produtos deste produtor precisarão de aprovação manual antes de serem publicados.">
              <FormControlLabel
                control={
                  <SwitchRoundSmall
                    sx={{ m: 1 }}
                    checked={
                      tenantSettings?.requestValidationForProducts ?? false
                    }
                    onChange={handleChangeRequestValidationForProducts}
                  />
                }
                label={
                  <Typography variant="subtitle2">
                    Exigir validação prévia para novos produtos
                  </Typography>
                }
                sx={{ width: '100%' }}
              />
            </HtmlTooltip>
          }
        />

        <ListItemDefault
          title={
            <HtmlTooltip title="Define se o produtor está autorizado a realizar vendas. Quando desativado, nenhum produto do produtor será carregado ou disponibilizado no checkout.">
              <FormControlLabel
                control={
                  <SwitchRoundSmall
                    sx={{ m: 1 }}
                    checked={tenantSettings?.salesEnabled ?? false}
                    onChange={handleChangeSalesEnabled}
                  />
                }
                label={
                  <Typography variant="subtitle2">Permite vender</Typography>
                }
                sx={{ width: '100%' }}
              />
            </HtmlTooltip>
          }
          widthTitlePercentage={50}
          variant="compact"
          value={
            <HtmlTooltip title="Quando ativado, o produtor poderá solicitar saques na plataforma, desde que a verificação de identidade esteja concluída.">
              <FormControlLabel
                control={
                  <SwitchRoundSmall
                    sx={{ m: 1 }}
                    checked={tenantSettings?.withdrawlsEnabled ?? false}
                    onChange={handleChangeWithdrawlsEnabled}
                  />
                }
                label={
                  <Typography variant="subtitle2">
                    Permite realizar saques
                  </Typography>
                }
                sx={{ width: '100%' }}
              />
            </HtmlTooltip>
          }
        />

        <ListItemDefault
          title={
            <HtmlTooltip title="Limite mensal acumulado de saque para contas CPF (soma dos saques feitos no mês corrente, resetado a cada mês). Quando atingido, o produtor não consegue mais solicitar saques até o próximo mês ou até o suporte alterar o valor.">
              <Typography variant="subtitle2">
                Limite mensal de saque (CPF)
              </Typography>
            </HtmlTooltip>
          }
          widthTitlePercentage={50}
          variant="compact"
          value={
            limitLoading ? (
              <CircularProgress size={20} sx={{ mx: 1.5 }} />
            ) : (
              <Box width="100%">
                <Stack direction="row" spacing={1} alignItems="center">
                  <SwitchRoundSmall
                    checked={tenantSettings?.withdrawLimitEnabled ?? false}
                    onChange={(e) => {
                      if (!tenantSettings) return
                      setErrorWithdrawLimit('')
                      setTenantSettings({
                        ...tenantSettings,
                        withdrawLimitEnabled: e.target.checked,
                      })
                    }}
                  />
                  <OutlinedInput
                    size="small"
                    value={maskMoney(tenantSettings?.withdrawLimit ?? 0)}
                    disabled={!tenantSettings?.withdrawLimitEnabled}
                    onChange={(e) => {
                      if (!tenantSettings) return
                      setErrorWithdrawLimit('')
                      setTenantSettings({
                        ...tenantSettings,
                        withdrawLimit: e.target.value as any,
                      })
                    }}
                    error={errorWithdrawLimit !== ''}
                    sx={{ maxWidth: 160 }}
                  />
                </Stack>
                {errorWithdrawLimit !== '' && (
                  <Typography variant="caption" color={red[700]}>
                    {errorWithdrawLimit}
                  </Typography>
                )}
              </Box>
            )
          }
        />

        <ListItemDefault
          title={
            <HtmlTooltip title="Quando ativado, o produtor poderá utilizar o Celetus Club como área de membros nos produtos.">
              <FormControlLabel
                control={
                  <SwitchRoundSmall
                    sx={{ m: 1 }}
                    checked={tenantSettings?.enableCeletusClub ?? false}
                    onChange={handleChangeCeletusClub}
                  />
                }
                label={
                  <Typography variant="subtitle2">
                    Habilitar Celetus Club
                  </Typography>
                }
                sx={{ width: '100%' }}
              />
            </HtmlTooltip>
          }
          widthTitlePercentage={50}
          variant="compact"
          value={
            tenantSettings?.enableCeletusClub &&
            tenantSettings?.clubProducerFriendlyId ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                ID: {tenantSettings.clubProducerFriendlyId}
              </Typography>
            ) : undefined
          }
        />

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            Pagamentos Internacionais
          </Typography>

          <Stack spacing={1.5}>
            <ListItemDefault
              title={
                <HtmlTooltip title="Quando ativado, o produtor poderá aceitar pagamentos em moedas internacionais via Stripe Connect.">
                  <FormControlLabel
                    control={
                      loadingInternational ? (
                        <CircularProgress size={20} sx={{ mx: 1.5 }} />
                      ) : (
                        <SwitchRoundSmall
                          sx={{ m: 1 }}
                          checked={
                            tenantSettings?.internationalSalesEnabled ?? false
                          }
                          onChange={handleChangeInternationalSales}
                        />
                      )
                    }
                    label={
                      <Typography variant="subtitle2">
                        Habilitar vendas internacionais
                      </Typography>
                    }
                    sx={{ width: '100%' }}
                  />
                </HtmlTooltip>
              }
              widthTitlePercentage={50}
              variant="compact"
              value={undefined}
            />
          </Stack>
        </Box>

        <Box sx={{ mt: 2, p: 1 }}>
          <AccountOperationalStatus
            canCreateProducts={tenantSettings?.enableRegisterProducts ?? false}
            canSell={tenantSettings?.salesEnabled ?? false}
            canWithdraw={tenantSettings?.withdrawlsEnabled ?? false}
            requirePreValidation={
              tenantSettings?.requestValidationForProducts ?? false
            }
          />
        </Box>

        <Button
          fullWidth
          variant="contained"
          size="large"
          startIcon={<LoginIcon />}
          sx={{ textTransform: 'none', mt: 2 }}
          onClick={handleChangeAccount}
        >
          Acessar Conta
        </Button>
      </Stack>

      <ErrorSnackbar open={error !== ''} onClose={() => setError('')}>
        {error}
      </ErrorSnackbar>
    </>
  )
}

export default UserSettingGeneral
